import { createContext, useCallback, useContext, useMemo, useState } from "react";
import KeyboardShortcutsHelp, { ShortcutDefinition } from "@/components/KeyboardShortcutsHelp";

interface ShortcutsContextValue {
  open: boolean;
  openModal: () => void;
  closeModal: () => void;
  setOpen: (open: boolean) => void;
  registerShortcuts: (shortcuts: ShortcutDefinition[]) => void;
}

const ShortcutsContext = createContext<ShortcutsContextValue | undefined>(undefined);

export const ShortcutsProvider = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [shortcuts, setShortcuts] = useState<ShortcutDefinition[]>([
    {
      keys: ["Ctrl/Cmd", "N"],
      description: "Create a new note",
      category: "Global",
    },
    {
      keys: ["Ctrl/Cmd", "K"],
      description: "Open search",
      category: "Global",
    },
  ]);

  const openModal = useCallback(() => setOpen(true), []);
  const closeModal = useCallback(() => setOpen(false), []);

  const registerShortcuts = useCallback((entries: ShortcutDefinition[]) => {
    setShortcuts((current) => {
      const byKey = new Map<string, ShortcutDefinition>();
      [...current, ...entries].forEach((entry) => {
        const signature = `${entry.category}-${entry.description}`;
        byKey.set(signature, entry);
      });
      return Array.from(byKey.values());
    });
  }, []);

  const value = useMemo(
    () => ({ open, openModal, closeModal, setOpen, registerShortcuts }),
    [open, openModal, closeModal, registerShortcuts],
  );

  return (
    <ShortcutsContext.Provider value={value}>
      {children}
      <KeyboardShortcutsHelp open={open} onOpenChange={setOpen} shortcuts={shortcuts} />
    </ShortcutsContext.Provider>
  );
};

export const useShortcutsModal = () => {
  const context = useContext(ShortcutsContext);
  if (!context) {
    throw new Error("useShortcutsModal must be used within a ShortcutsProvider");
  }
  return context;
};
